// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useState } from "react";
import { useTeamsEnabled } from "../../context/FeatureFlagsContext";

import {
  ContactCard24Regular,
  Map24Regular,
  ChatMultiple24Regular,
  Building24Regular,
  PersonBoard24Regular,
  Wrench24Regular,
  ArrowTrendingLines24Regular,
  PersonArrowRight24Regular,
  PersonHeart24Regular,
  BrainCircuit24Regular,
  DocumentText24Regular,
  PanelLeftExpand24Regular,
  PanelLeftContract24Regular,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Body1,
  CounterBadge,
  NavDrawer,
  NavDrawerHeader,
  NavDrawerBody,
  NavItem,
} from "@fluentui/react-components";
import {
  appRoutes,
  type AppRoute,
  type AppRouteKey,
} from "../../types/routing";
import { navigateTo } from "../../router/navigation";
import {
  useMediaQuery,
  COMPACT_VIEWPORT_QUERY,
} from "../../hooks/useMediaQuery";
import { useWorkspaceFocus } from "../../hooks/useWorkspaceFocus";
import { useAppLocale } from "../../context/AppLocaleContext";

interface NavigationRailRouteMetadata {
  icon: React.ReactNode;
  surface?: "property" | "sales" | "team";
}

const NAV_ROUTE_LABELS: Partial<
  Record<AppRouteKey, NavigationRailRouteMetadata>
> = {
  contacts: {
    icon: <ContactCard24Regular />,
  },
  journeys: {
    icon: <Map24Regular />,
  },
  prospecting: {
    icon: <PersonBoard24Regular />,
    surface: "sales",
  },
  communications: {
    icon: <ChatMultiple24Regular />,
  },
  property: {
    icon: <Building24Regular />,
    surface: "property",
  },
  vendors: {
    icon: <PersonBoard24Regular />,
    surface: "property",
  },
  workOrders: {
    icon: <Wrench24Regular />,
    surface: "property",
  },
  workloadForecasts: {
    icon: <ArrowTrendingLines24Regular />,
    surface: "property",
  },
  delegations: {
    icon: <PersonArrowRight24Regular />,
    surface: "team",
  },
  relationships: {
    icon: <PersonHeart24Regular />,
  },
  insights: {
    icon: <BrainCircuit24Regular />,
  },
  documents: {
    icon: <DocumentText24Regular />,
  },
};

const useStyles = makeStyles({
  drawer: {
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "var(--riddle-surface-elevated)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: tokens.shadow4,
    boxSizing: "border-box",
  },
  drawerInline: {
    width: "224px",
    alignSelf: "start",
    position: "sticky",
    top: tokens.spacingVerticalL,
  },
  drawerInlineCompact: {
    width: "64px",
  },
  drawerOverlay: {
    width: "280px",
    maxWidth: "82vw",
    borderTopLeftRadius: "0",
    borderBottomLeftRadius: "0",
  },
  header: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  headerCompact: {
    gridTemplateColumns: "auto",
    justifyContent: "end",
  },
  railTitle: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  compactToggle: {
    minWidth: "unset",
    width: "32px",
    height: "32px",
    padding: "0",
    color: tokens.colorNeutralForeground3,
  },
  body: {
    display: "grid",
    gap: tokens.spacingVerticalXXS,
    padding: `0 ${tokens.spacingHorizontalXS} ${tokens.spacingVerticalS}`,
  },
  navItem: {
    borderRadius: tokens.borderRadiusLarge,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    minHeight: "44px",
    height: "auto",
    width: "100%",
    textAlign: "left",
    border: `1px solid ${tokens.colorTransparentStroke}`,
    backgroundColor: "transparent",
    cursor: "pointer",
    color: tokens.colorNeutralForeground1,
  },
  navItemCompact: {
    justifyContent: "center",
  },
  navItemActive: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorBrandBackground2,
    boxShadow: tokens.shadow4,
  },
  navItemIconWell: {
    position: "relative",
    width: "36px",
    height: "36px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  navItemIconWellActive: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  navBadge: {
    position: "absolute",
    top: "-6px",
    right: "-8px",
    pointerEvents: "none",
  },
  navItemCopy: {
    display: "grid",
    gap: "2px",
    alignContent: "center",
    flex: "1 1 auto",
    minWidth: 0,
  },
  navItemTitle: {
    color: tokens.colorNeutralForeground1,
  },
});

interface NavigationRailProps {
  activeRouteKey: AppRouteKey;
  isAuthenticated: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  delegationBadgeCount?: number;
}

export function NavigationRail({
  activeRouteKey,
  isAuthenticated,
  open = false,
  onOpenChange,
  delegationBadgeCount = 0,
}: NavigationRailProps) {
  const styles = useStyles();
  const { copy } = useAppLocale();
  const [isRailCollapsed, setIsRailCollapsed] = useState<boolean>(true);
  const isCompactViewport = useMediaQuery(COMPACT_VIEWPORT_QUERY);
  const { showsPropertySurfaces, showsSalesSurfaces, isSolo } =
    useWorkspaceFocus();
  const { teams } = useTeamsEnabled();

  function isSurfaceVisible(route: AppRoute): boolean {
    const surface = NAV_ROUTE_LABELS[route.key]?.surface;

    if (surface === "property") {
      return showsPropertySurfaces;
    }
    if (surface === "sales") {
      return showsSalesSurfaces;
    }
    if (surface === "team") {
      return teams && !isSolo;
    }

    return true;
  }

  const routes = appRoutes
    .filter(
      (route) =>
        route.key !== "login" &&
        route.key !== "onboarding" &&
        route.key !== "settings" &&
        route.key !== "tasks",
    )
    .filter(isSurfaceVisible);

  const drawerType = isCompactViewport ? "overlay" : "inline";
  const isDrawerOpen = isCompactViewport ? open : true;
  const showLabels = isCompactViewport || !isRailCollapsed;

  function handleSelect(route: AppRoute) {
    navigateTo(route.path);
    if (isCompactViewport) {
      onOpenChange?.(false);
    }
  }

  return (
    <NavDrawer
      open={isDrawerOpen}
      type={drawerType}
      role="navigation"
      aria-label={copy.primaryNavigation}
      onOpenChange={(_, data) => onOpenChange?.(data.open)}
      className={mergeClasses(
        styles.drawer,
        !isCompactViewport && styles.drawerInline,
        !isCompactViewport && isRailCollapsed && styles.drawerInlineCompact,
        isCompactViewport && styles.drawerOverlay,
      )}
    >
      <NavDrawerHeader
        className={mergeClasses(
          styles.header,
          !isCompactViewport && isRailCollapsed && styles.headerCompact,
        )}
      >
        {isCompactViewport ? (
          <>
            <Body1 className={styles.railTitle}>Riddle workspace</Body1>
            <Button
              className={styles.compactToggle}
              appearance="subtle"
              size="small"
              onClick={() => onOpenChange?.(false)}
              aria-label={copy.closeNavigation}
              title={copy.closeNavigation}
              icon={<Dismiss24Regular />}
            />
          </>
        ) : (
          <>
            {!isRailCollapsed && (
              <Body1 className={styles.railTitle}>Riddle workspace</Body1>
            )}
            <Button
              className={styles.compactToggle}
              appearance="subtle"
              size="small"
              onClick={() => setIsRailCollapsed((previous) => !previous)}
              aria-label={
                isRailCollapsed ? copy.expandNavigation : copy.collapseNavigation
              }
              title={
                isRailCollapsed ? copy.expandNavigation : copy.collapseNavigation
              }
              icon={
                isRailCollapsed ? (
                  <PanelLeftExpand24Regular />
                ) : (
                  <PanelLeftContract24Regular />
                )
              }
            />
          </>
        )}
      </NavDrawerHeader>

      <NavDrawerBody className={styles.body}>
        {routes.map((route) => {
          const isActive = route.key === activeRouteKey;
          const isDisabled = route.requiresAuth && !isAuthenticated;
          const label = copy.nav[route.key];
          const badgeCount =
            route.key === "delegations" ? delegationBadgeCount : 0;
          const baseLabel = label?.desc
            ? `${label.short}. ${label.desc}`
            : (label?.short ?? route.title);
          const ariaLabel =
            badgeCount > 0
              ? `${baseLabel} (${badgeCount} pending)`
              : baseLabel;

          return (
            <NavItem
              key={route.key}
              value={route.key}
              disabled={isDisabled}
              onClick={() => handleSelect(route)}
              aria-current={isActive ? "page" : undefined}
              aria-label={ariaLabel}
              title={ariaLabel}
              data-tour-step={`nav-${route.key}`}
              className={mergeClasses(
                styles.navItem,
                !showLabels && styles.navItemCompact,
                isActive && styles.navItemActive,
              )}
              icon={
                <span
                  className={mergeClasses(
                    styles.navItemIconWell,
                    isActive && styles.navItemIconWellActive,
                  )}
                  aria-hidden="true"
                >
                  {NAV_ROUTE_LABELS[route.key]?.icon}
                  {badgeCount > 0 && (
                    <CounterBadge
                      count={badgeCount}
                      size="small"
                      className={styles.navBadge}
                    />
                  )}
                </span>
              }
            >
              {showLabels && (
                <span className={styles.navItemCopy}>
                  <Body1 className={styles.navItemTitle}>
                    {label?.short ?? route.title}
                  </Body1>
                </span>
              )}
            </NavItem>
          );
        })}
      </NavDrawerBody>
    </NavDrawer>
  );
}
